import { _ } from "../../../../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import RuleModelFieldVisitor from "./RuleModelFieldVisitor.js";

/**
 * Shows a {@link RuleTemplate}'s details: its type of rule model, a
 * sentence describing what this rule model is about, and a sentence
 * explaining the impact of this rule template on the forecast.
 *
 * If `inheritanceLabel` is provided, a link is rendered with this
 * label. Clicking on this link triggers a call to
 * `onInheritanceLinkClick`
 *
 * @param {RuleTemplate} ruleTemplate - The RuleTemplate to show
 *   information for
 * @param {renderable} inheritanceLabel - The content of the link
 * @param {function} onInheritanceLinkClick - A function called with
 *   no argument if `inheritanceLabel` is clicked
 * @param {Budget[]} budgets - All budgets the user can pick
 *   for a FromBudgetPredictionRuleModel
 */
export default class RuleTemplateInfoWidget extends Widget {
	constructor({
		ruleTemplate,
		inheritanceLabel,
		onInheritanceLinkClick,
		budgets,
	}) {
		super(...arguments);

		this._ruleTemplate = ruleTemplate;
		this._inheritanceLabel = inheritanceLabel;
		this._onInheritanceLinkClick = onInheritanceLinkClick;
		this._budgets = budgets;
	}

	renderContentOn(html) {
		html.div(
			{ class: "rule-template-info" },
			this._renderRuleModelHeaderOn.bind(this),
			this._renderRuleModelFieldsOn.bind(this),
			this._renderInheritanceSentenceOn.bind(this),
		);
	}

	_renderRuleModelHeaderOn(html) {
		html.div(
			{ class: "rule-model-header" },
			html.h4(
				{ class: "rule-model-name" },
				this._ruleTemplate.getRuleModelLabel(),
			),
			html.div(
				{ class: "rule-model-description" },
				this._ruleTemplate.getRuleModelDescriptionText(),
			),
		);
	}

	_renderRuleModelFieldsOn(html) {
		html.div(
			{ class: "rule-model-fields" },
			this._renderFieldsOn.bind(this),
		);
	}

	_renderFieldsOn(html) {
		html.render(
			new RuleModelFieldVisitor({ budgets: this._budgets }).visit(
				this._ruleTemplate.getRuleModel(),
			),
		);
	}

	_renderInheritanceSentenceOn(html) {
		if (!this._inheritanceLabel) {
			return;
		}

		html.p(
			{ class: "rule-template-inheritance" },
			_("Inherited from {link}.", {
				link: this._renderInheritanceLinkOn.bind(this),
			}),
		);
	}

	_renderInheritanceLinkOn(html) {
		if (!this._onInheritanceLinkClick) {
			html.render(this._inheritanceLabel);
			return;
		}

		html.a(
			{ click: () => this._onInheritanceLinkClick() },
			this._inheritanceLabel,
		);
	}
}
